import { InferGetStaticPropsType, GetStaticPaths, GetStaticProps } from 'next';
import { NextSeo, ArticleJsonLd, BreadcrumbJsonLd } from 'next-seo';
import { AppRoutes } from 'src/lib/routes';
import { City, Country, Blog, TravelItinerary, Translation } from 'src/types';
import ApiClient from 'src/lib/api_client';
import { DBDateFormat } from 'src/lib/constants';
import { startCase } from 'lodash-es';
import WithDefaultJsonLd from 'src/components/WithDefaultJsonLd';
import dayjs from 'dayjs';
import RevalidateSettings from 'src/lib/revalidate_settings';
import BlogLayout from 'src/components/BlogLayout';
import { useTranslations } from 'next-intl';
import { prepareTranslations } from 'src/lib/prepare_translations';
import useAddLocaleToUrl from 'src/hooks/useAddLocaleToUrl';

const metaTitle = (country: Country | null, kind: string | null) => {
  return country ? `${country.name}` : kind ? startCase(kind) : '';
};

const Page: React.FC<InferGetStaticPropsType<typeof getStaticProps>> = ({
  kind,
  country,
  countries,
  mostReadBlogs,
  featuredBlog,
  travelItineraries,
  blogsCount
}) => {
  const addLocaleToUrl = useAddLocaleToUrl()
  const translation = useTranslations('pages/magazine/[cate]/index')
  const meteDesc = country ? translation('metaTitleCountryName', {countryName: country.name}) : translation('metaTitle')

  const breadcrumbs = () => {
    if (country) {
      return [
        {
          item: addLocaleToUrl(AppRoutes.rootRoute({}).toUrl()),
          name: translation('home'),
          position: 1,
        },
        {
          item:
            country.slug === 'singapore'
              ? addLocaleToUrl(AppRoutes.cityRoute({ id: country.slug }).toUrl())
              : addLocaleToUrl(AppRoutes.countryRoute({ id: country.slug }).toUrl()),
          name: country.name,
          position: 2,
        },
        {
          item: addLocaleToUrl(AppRoutes.magazineCateRoute({ cate: country.slug }).toUrl()),
          name: country.name,
          position: 3,
        },
      ];
    } else {
      return [];
    }
  };

  return (
    <>
      <NextSeo
        title={metaTitle(country, kind)}
        description={meteDesc}
        canonical={addLocaleToUrl(AppRoutes.magazineCateRoute({ cate: country ? country.slug : kind ? kind : '' }).toUrl())}
        openGraph={{
          title: metaTitle(country, kind),
          description: meteDesc,
          url: addLocaleToUrl(AppRoutes.magazineCateRoute({ cate: country ? country.slug : kind ? kind : '' }).toUrl()),
        }}
      />

      <WithDefaultJsonLd>
        <BreadcrumbJsonLd itemListElements={breadcrumbs()}/>

        <ArticleJsonLd
          title={metaTitle(country, kind)}
          authorName="Seek Sophie"
          url={addLocaleToUrl(AppRoutes.magazineCateRoute({ cate: country ? country.slug : kind ? kind : '' }).toUrl())}
          description={meteDesc}
          datePublished={dayjs().format(DBDateFormat)}
          images={[]}
        />
      </WithDefaultJsonLd>

      <BlogLayout
        country={country}
        countries={countries}
        featuredBlog={featuredBlog}
        mostReadBlogs={mostReadBlogs}
        travelItineraries={travelItineraries}
        blogsCount={blogsCount}
      />
    </>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  if (process.env.APP_ENV === 'development') {
    return {
      paths: [],
      fallback: 'blocking'
    }
  }

  const [{ data: countries }] = await ApiClient.getCountries({});
  const countryParams = countries.map((country) => {
    return { params: { cate: country.slug } }
  })

  return {
    paths: countryParams,
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps<{
  kind: string | null;
  country: Country | null;
  cities: City[];
  travelItineraries: TravelItinerary[];
  countries: Country[];
  mostReadBlogs: Blog[];
  featuredBlog: Blog | null;
  blogsCount: number;
}> = async (context) => {
  const { cate } = context.params || {};

  if (!cate) {
    return {
      notFound: true,
      revalidate: RevalidateSettings.medium
    };
  }

  const translations = await prepareTranslations(context, ['pages/magazine/[cate]/index']);
  const [country] = await ApiClient.getCountry(
    { id: String(cate) },
    { 'X-NEXT-LOCALE': translations.locale }
  );
  const kind = null

  if (country && 'error' in country) {
    return {
      notFound: true,
      revalidate: RevalidateSettings.medium
    };
  }

  const [{ data: cities = [] }] = await ApiClient.getCities({
    q: { options_linked_with_blogs_existed_true: 1 },
    page: 1,
    per_page: 10,
  });

  const [{ data: featuredBlogs = [], pagination }] = await ApiClient.getBlogs({
    country_id: country?.id,
    sort_by: ['featured', 'latest'],
    page: 1,
    per_page: 1,
  }, { 'X-NEXT-LOCALE': translations.locale });

  const [{ data: mostReadBlogs = [] }] = await ApiClient.getBlogs({
    country_id: country?.id,
    sort_by: 'most_read',
    page: 1,
    per_page: 4,
  }, { 'X-NEXT-LOCALE': translations.locale });

  const [{ data: travelItineraries }] = await ApiClient.getTravelItineraries({
    q: {
      g: {
        0: {
          status_eq: 'published'
        },
        1: {
          g: {
            0: {
              country_id_eq: country.id,
            },
            1: {
              travel_itinerary_cities_city_country_id_eq: country.id,
            }
          },
          m: 'or',
        },
      },
      s: ['country_id desc', 'views_count desc'],
    },
    page: 1,
    per_page: 20,
  }, { 'X-NEXT-LOCALE': translations.locale });

  const [{ data: countries }] = await ApiClient.getCountries({
    q: {
      published_at_not_null: 1,
      options_linked_with_blogs_existed_true: 1
    },
  }, { 'X-NEXT-LOCALE': translations.locale });

  return {
    props: {
      blogsCount: pagination.totalCount,
      kind: kind,
      country: country,
      countries: countries,
      cities: cities,
      featuredBlog: featuredBlogs[0] || null,
      mostReadBlogs: mostReadBlogs,
      travelItineraries: travelItineraries,
      ...translations
    },
    revalidate: RevalidateSettings.medium
  };
};

export default Page;
